import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
  IconButton,
  Modal,
  TextField,
  Snackbar,
  Alert as MuiAlert,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getMatchLiveData,
  PushMatchLiveData,
  updateLiveTaggingStatus,
  getPlayersRelatedToTeam,
} from "../config/mainRequest";
import { useParams, useLocation, useHistory } from "react-router-dom";
import {
  subscribeToLineupChannel,
  unsubscribeFromLineupChannel,
} from "../utils/ablyManager";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import { Check } from "@mui/icons-material";
const PLAYED_IN_STATUSES = [3, 6, 7, 8, 9];

const stats = {
  passing: "Passing",
  assists: "Assists",
  shots_off_target: "Shots off Target",
  own_goal: "Own Goal",
  fouls: "Fouls",
  chances_created: "Chances Created",
  shots_on_target: "Shots on Target",
  corners: "Corners",
  offside: "Offside",
  penalty: "Penalty",
  goal: "Goal",
  yellow_card: "Yellow Card",
  red_card: "Red Card",
};

function LiveTaggingBoard() {
  const { matchId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const matchStatus = location.state?.matchStatus;
  const liveTaggingStatus = location.state?.liveTaggingStatus;
  const { token } = useSelector((state) => ({
    token: state.auth.token,
  }));

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // fetching keys from the stats object to match the api payload as initialisation.
  const [counts, setCounts] = useState({
    A: Object.fromEntries(Object.keys(stats).map((key) => [key, 0])),
    B: Object.fromEntries(Object.keys(stats).map((key) => [key, 0])),
  });
  const [cardDetails, setCardDetails] = useState({
    A: {
      goal: [],
      yellow_card: [],
      red_card: [],
      penalty: [],
      own_goal: [],
    },
    B: {
      goal: [],
      yellow_card: [],
      red_card: [],
      penalty: [],
      own_goal: [],
    },
  });
  const [openModalMarkComplete, setOpenModalMarkComplete] = useState(false);
  const [openModalCounterControl, setOpenModalCounterControl] = useState(false);
  const [inputMode, setInputMode] = useState({ A: false, B: false });
  const [modalStatKey, setModalStatKey] = useState("");
  const [modalTeam, setModalTeam] = useState("");
  const [modalData, setModalData] = useState([]);
  const [liveMatchData, setLiveMatchData] = useState(null);
  const [isAddNew, setIsAddNew] = useState(false);
  const [hasPushedFirstTime, setHasPushedFirstTime] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState({ A: [], B: [] });

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  useEffect(() => {
    const fetchLiveData = async () => {
      const response = await getMatchLiveData({ matchId });
      if (response) {
        setLiveMatchData(response);

        const teamA = response?.team_a_details?.live_analysis_data;
        const teamB = response?.team_b_details?.live_analysis_data;

        setCardDetails({
          A: {
            goal: teamA?.goal?.details || [],
            yellow_card: teamA?.yellow_card?.details || [],
            red_card: teamA?.red_card?.details || [],
            penalty: teamA?.penalty?.details || [],
            own_goal: teamA?.own_goal?.details || [],
          },
          B: {
            goal: teamB?.goal?.details || [],
            yellow_card: teamB?.yellow_card?.details || [],
            red_card: teamB?.red_card?.details || [],
            penalty: teamB?.penalty?.details || [],
            own_goal: teamB?.own_goal?.details || [],
          },
        });
      }
    };

    if (matchId) {
      fetchLiveData();
    }
  }, [matchId]);

  // Fetch squad for a team to power the jersey-number dropdown.
  const fetchTeamPlayers = useCallback(
    async (team) => {
      const normalize = (list) =>
        (Array.isArray(list) ? list : [])
          .map((p) => {
            const umd = p.user_match_details || {};
            // Only starting XI (1) and substitutes (2) — exclude bench (null).
            if (![1, 2].includes(umd.lineup_notation)) return null;
            const jersey = umd.jersey_number ?? p.jersey_number;
            if (jersey === undefined || jersey === null || jersey === "")
              return null;
            const name =
              `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
              `#${jersey}`;
            return { jersey_no: String(jersey), label: `#${jersey} — ${name}` };
          })
          .filter(Boolean);

      const details =
        team === "A"
          ? liveMatchData?.team_a_details
          : liveMatchData?.team_b_details;
      const teamId = details?.team_id ?? details?.id;
      if (!teamId || !matchId) return;

      const playedInMatch = PLAYED_IN_STATUSES.includes(matchStatus);

      const list = await getPlayersRelatedToTeam({
        teamId,
        matchId,
        token,
        playedInMatch,
      });
      setTeamPlayers((prev) => ({ ...prev, [team]: normalize(list) }));
    },
    [liveMatchData, matchId, token, matchStatus]
  );

  useEffect(() => {
    if (liveMatchData && matchId) {
      fetchTeamPlayers("A");
      fetchTeamPlayers("B");
    }
  }, [liveMatchData, matchId, fetchTeamPlayers]);

  // Keep the squad live via Ably: refetch when lineup mappings change.
  useEffect(() => {
    if (!matchId || !liveMatchData) return;

    subscribeToLineupChannel(matchId, () => {
      fetchTeamPlayers("A");
      fetchTeamPlayers("B");
    });

    return () => unsubscribeFromLineupChannel(matchId);
  }, [matchId, liveMatchData, fetchTeamPlayers]);

  const isAllZero = () => {
    const teamKeys = ["A", "B"];

    return teamKeys.every((team) => {
      const teamStats = counts?.[team] || {};

      return Object.keys(teamStats).every((key) => teamStats[key] === 0);
    });
  };

  const pushLiveData = async () => {
    if (isAllZero()) return;

    const getTeamStats = (teamKey) => {
      const stats = { ...counts[teamKey] };

      ["goal", "yellow_card", "red_card", "penalty", "own_goal"].forEach(
        (key) => {
          const details = cardDetails[teamKey]?.[key] || [];
          stats[key] = {
            count: details?.length,
            details,
          };
        }
      );

      return stats;
    };

    const teamAStats = getTeamStats("A");
    const teamBStats = getTeamStats("B");

    await PushMatchLiveData({
      matchId,
      teamAStats,
      teamBStats,
    });

  };

  const markTaggingStarted = useCallback(async () => {
    if (hasPushedFirstTime) return;
    if (liveTaggingStatus !== 3) {
      try {
        const res = await updateLiveTaggingStatus({
          match_id: matchId,
          live_tagging_status: 2,
          token: token,
        });
        if (res?.response_message !== "") {
          setHasPushedFirstTime(true);
        }
      } catch (error) {
        console.error("Error updating tagging status:", error);
      }
    }
  }, [hasPushedFirstTime, matchId, token, liveTaggingStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      pushLiveData();
    }, 15000);

    return () => clearInterval(interval);
  }, [counts, matchId, hasPushedFirstTime]);

  useEffect(() => {
    if (liveMatchData) {
      const teamAStats = parseStatsFromLiveData(
        liveMatchData?.team_a_details?.live_analysis_data
      );
      const teamBStats = parseStatsFromLiveData(
        liveMatchData?.team_b_details?.live_analysis_data
      );

      setCounts({
        A: teamAStats,
        B: teamBStats,
      });
    }
  }, [liveMatchData]);

  const handleUpdateClick = async () => {
    const getTeamStats = (teamKey) => {
      const stats = { ...counts[teamKey] };

      ["goal", "yellow_card", "red_card", "penalty", "own_goal"].forEach(
        (key) => {
          const details = cardDetails[teamKey]?.[key] || [];
          stats[key] = {
            count: details?.length,
            details,
          };
        }
      );

      return stats;
    };

    const teamAStats = getTeamStats("A");
    const teamBStats = getTeamStats("B");

    try {
      const res = await PushMatchLiveData({
        matchId,
        teamAStats,
        teamBStats,
      });

      if (res?.response_data === true) {
        showSnackbar("Data updated Successfully!", "success");
        markTaggingStarted();
      } else {
        alert("Something went wrong while saving.");
      }
    } catch (error) {
      console.error("Update failed:", error);
      alert("An error occurred while saving data.");
    }
  };

  const parseStatsFromLiveData = (liveData) => {
    const result = {};

    Object.keys(stats).forEach((statKey) => {
      const statValue = (liveData || {})[statKey];

      if (statValue && typeof statValue === "object") {
        result[statKey] = Array.isArray(statValue.details)
          ? statValue.details.length
          : statValue.count ?? 0;
      } else {
        result[statKey] = statValue || 0;
      }
    });

    return result;
  };

  const handleIncrement = (statKey, team) => {
    if (
      ["goal", "yellow_card", "red_card", "penalty", "own_goal"].includes(
        statKey
      )
    ) {
      handleOpenModalCounterControl(statKey, team, true);
    } else {
      setCounts((prev) => ({
        ...prev,
        [team]: {
          ...prev[team],
          [statKey]: (prev[team][statKey] || 0) + 1,
        },
      }));
    }
  };

  const handleDecrement = (statKey, team) => {
    setCounts((prev) => ({
      ...prev,
      [team]: {
        ...prev[team],
        [statKey]: Math.max(0, (prev[team][statKey] || 0) - 1),
      },
    }));
  };

  const handleOpenModal = () => {
    setOpenModalMarkComplete(true);
  };

  const handleCloseModal = () => {
    setOpenModalMarkComplete(false);
  };

  const handleAccept = async () => {
    try {
      const res = await updateLiveTaggingStatus({
        match_id: matchId,
        live_tagging_status: 3,
        token: token,
      });

      if (res?.response_message !== "") {
        showSnackbar("Marked Completed!", "success");
        setOpenModalMarkComplete(false);
        history.push("/home");
      }
    } catch (error) {
      console.error("Error updating tagging status:", error);
    }
  };

  const handleOpenModalCounterControl = (statKey, team, isAddNew) => {
    setModalStatKey(statKey);
    setModalTeam(team);
    setIsAddNew(isAddNew);

    const currentDetails = cardDetails[team]?.[statKey] || [];

    const updatedDetails = isAddNew
      ? [{ time: "", jersey_no: "" }]
      : [...currentDetails];

    setModalData(JSON.parse(JSON.stringify(updatedDetails)));
    setOpenModalCounterControl(true);
  };

  const handleCloseModalCounterControl = () => {
    setOpenModalCounterControl(false);
  };

  const ModeToggle = ({ team }) => {
    const accent = team === "A" ? "#1A56DB" : "#FF5A1F";
    return (
      <Box
        sx={{
          display: "flex",
          border: "1px solid var(--border-1)",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        {[
          ["Counter", false],
          ["Input", true],
        ].map(([lbl, val]) => {
          const activeMode = inputMode[team] === val;
          return (
            <Button
              key={lbl}
              onClick={() => setInputMode((prev) => ({ ...prev, [team]: val }))}
              sx={{
                textTransform: "none",
                fontSize: "10px",
                fontWeight: "bold",
                padding: "2px 8px",
                minWidth: "auto",
                borderRadius: 0,
                lineHeight: 1.4,
                backgroundColor: activeMode ? accent : "var(--surface-1)",
                color: activeMode ? "#fff" : "var(--text-1)",
                "&:hover": {
                  backgroundColor: activeMode ? accent : "var(--surface-2)",
                },
              }}
            >
              {lbl}
            </Button>
          );
        })}
      </Box>
    );
  };

  const BoxItem = ({ statKey, team }) => {
    const label = stats[statKey];
    const currentCount = counts[team]?.[statKey] ?? 0;
    const [inputVal, setInputVal] = useState(String(currentCount));

    const applyInput = () => {
      const n = Math.max(0, parseInt(inputVal, 10) || 0);
      setInputVal(String(n));
      setCounts((prev) => ({
        ...prev,
        [team]: { ...prev[team], [statKey]: n },
      }));
    };

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid var(--border-1)",
          backgroundColor: "var(--surface-2)",
          padding: "8px 12px",
          borderRadius: "8px",
          flexGrow: 1,
          minWidth: "0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {["goal", "yellow_card", "red_card", "penalty", "own_goal"].includes(
            statKey
          ) && (
            <IconButton
              onClick={() =>
                handleOpenModalCounterControl(statKey, team, false)
              }
              sx={{
                padding: 0,
                color: "#1A56DB",
                fontSize: "4px",
              }}
            >
              <EditNoteIcon fontSize="small" />
            </IconButton>
          )}
          <Typography sx={{ fontSize: "10px", fontWeight: "regular", color: "var(--text-1)" }}>
            {label}
          </Typography>
        </Box>

        {inputMode[team] &&
        !["goal", "yellow_card", "red_card", "penalty", "own_goal"].includes(
          statKey,
        ) ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <TextField
              type="text"
              inputMode="numeric"
              value={inputVal}
              onChange={(e) =>
                setInputVal(e.target.value.replace(/\D/g, ""))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") applyInput();
              }}
              inputProps={{ min: 0, inputMode: "numeric", pattern: "[0-9]*" }}
              size="small"
              sx={{
                width: "56px",
                backgroundColor: "var(--surface-1)",
                borderRadius: "4px",
                "& .MuiInputBase-input": {
                  padding: "3px 6px",
                  fontSize: "12px",
                  textAlign: "center",
                  color: "var(--text-1)",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--border-1)",
                },
              }}
            />
            <Button
              variant="contained"
              onClick={applyInput}
              sx={{
                minWidth: "auto",
                height: "24px",
                padding: "0 8px",
                fontSize: "11px",
                fontWeight: "bold",
                lineHeight: 1,
                textTransform: "none",
                backgroundColor: team === "A" ? "#1A56DB" : "#FF5A1F",
                color: "#fff",
              }}
            >
              <Check sx={{ fontSize: 14 }} />
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                minWidth: "24px",
                height: "24px",
                backgroundColor: "var(--surface-1)",
                borderColor: "var(--border-1)",
                color: "var(--text-1)",
                fontSize: "12px",
                fontWeight: "bold",
                padding: 0,
                lineHeight: 1,
                minHeight: "auto",
              }}
              onClick={() => handleDecrement(statKey, team)}
              disabled={[
                "goal",
                "yellow_card",
                "red_card",
                "penalty",
                "own_goal",
              ].includes(statKey)}
            >
              -
            </Button>

            <Box
              sx={{
                minWidth: "28px",
                height: "24px",
                backgroundColor: team === "A" ? "#1A56DB" : "#FF5A1F",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              {counts[team]?.[statKey] ?? 0}
            </Box>

            <Button
              variant="outlined"
              sx={{
                minWidth: "24px",
                height: "24px",
                backgroundColor: "var(--surface-1)",
                borderColor: "var(--border-1)",
                color: "var(--text-1)",
                fontSize: "12px",
                fontWeight: "bold",
                padding: 0,
                lineHeight: 1,
                minHeight: "auto",
              }}
              onClick={() => handleIncrement(statKey, team)}
            >
              +
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const MarkCompleteModal = () => {
    return (
      <Modal open={openModalMarkComplete} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            backgroundColor: "var(--surface-1)",
            border: "1px solid var(--border-1)",
            borderRadius: "8px",
            padding: "20px",
            boxShadow: 24,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Mark Complete
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ marginTop: "8px", marginBottom: "16px" }}
          >
            Are you sure you want to mark this live match analysis as complete?
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              paddingX: "20px",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                flex: 1,
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "var(--border-1)",
                backgroundColor: "var(--surface-2)",
                color: "var(--text-1)",
                fontWeight: "medium",
                fontSize: "14px",
              }}
              onClick={handleCloseModal}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                flex: 1,
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#1A56DB",
                color: "#fff",
                fontWeight: "medium",
                fontSize: "14px",
              }}
              onClick={handleAccept}
            >
              I accept
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  const GenericSnackbar = ({
    open,
    onClose,
    message,
    severity = "success",
    duration = 3000,
  }) => {
    return (
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={onClose} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    );
  };

  const showSnackbar = (msg, severity = "success") => {
    setSnackbar({ open: true, message: msg, severity });
  };

  return (
    <Box sx={{ minHeight: "100vh", overflowY: "auto", backgroundColor: "var(--app-bg)" }}>
      <CounterControlModal
        openModalCounterControl={openModalCounterControl}
        modalData={modalData}
        handleCloseModalCounterControl={handleCloseModalCounterControl}
        isAddNew={isAddNew}
        modalStatKey={modalStatKey}
        cardDetails={cardDetails}
        modalTeam={modalTeam}
        setCardDetails={setCardDetails}
        setCounts={setCounts}
        players={teamPlayers[modalTeam] || []}
      />
      <Box
        sx={{
          backgroundColor: "#000",
          color: "#fff",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 1000,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 1,
          }}
        >
          <Typography
            variant="body1"
            fontWeight="medium"
            color="#3F83F8"
            noWrap
          >
            {matchId}
          </Typography>

          <Typography variant="body1" fontWeight="medium" color="#fff" noWrap>
            {`${liveMatchData?.team_a_details?.team_name ?? "Team A"} vs ${liveMatchData?.team_b_details?.team_name ?? "Team B"}`}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          fontWeight="regular"
          color="#9CA3AF"
          sx={{ flexShrink: 0 }}
        >
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </Box>

      <Container
        sx={{
          display: "flex",
          flexDirection: "row",
          marginTop: "40px",
          justifyContent: "space-between",
          gap: "10px",
          padding: "20px",
        }}
      >
        {/* Box 1 */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "var(--surface-1)",
            border: "1px solid var(--border-1)",
            boxShadow: "var(--shadow-1)",
            borderRadius: "8px",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ color: "var(--text-1)" }}>
                {liveMatchData?.team_a_details?.team_name}
              </Typography>
              <ModeToggle team="A" />
            </Box>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ color: "var(--text-2)" }}>
              Team A
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "var(--border-1)", marginY: "8px" }} />

          <Grid
            container
            spacing={0.8}
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {Object.keys(stats).map((statKey) => (
              <Grid item xs={6} key={statKey} sx={{ display: "flex" }}>
                <BoxItem statKey={statKey} team="A" />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Box 2 */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "var(--surface-1)",
            border: "1px solid var(--border-1)",
            boxShadow: "var(--shadow-1)",
            borderRadius: "8px",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" fontWeight="medium" sx={{ color: "var(--text-2)" }}>
              Team B
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <ModeToggle team="B" />
              <Typography variant="subtitle1" fontWeight="medium" sx={{ color: "var(--text-1)" }}>
                {liveMatchData?.team_b_details?.team_name}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: "var(--border-1)", marginY: "8px" }} />

          <Grid
            container
            spacing={0.8}
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {Object.keys(stats).map((statKey) => (
              <Grid item xs={6} key={statKey} sx={{ display: "flex" }}>
                <BoxItem statKey={statKey} team="B" />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Box
        sx={{
          display: "flex",
          gap: "10px",
          width: "100%",
          padding: "0 20px",
          boxSizing: "border-box",
          marginBottom: "10px",
        }}
      >
        <Button
          onClick={handleUpdateClick}
          variant="outlined"
          fullWidth
          sx={{
            borderColor: "var(--border-1)",
            backgroundColor: "var(--surface-1)",
            color: "var(--text-1)",
            fontWeight: "medium",
            fontSize: "14px",
            padding: "8px 12px",
            textTransform: "none",
            height: "36px",
            borderRadius: "8px",
          }}
        >
          Update
        </Button>

        <Button
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: "#1A56DB",
            color: "#fff",
            fontWeight: "medium",
            fontSize: "14px",
            padding: "8px 12px",
            textTransform: "none",
            height: "36px",
            "&:hover": {
              backgroundColor: "#174BB6",
            },
            borderRadius: "8px",
          }}
          onClick={handleOpenModal}
        >
          Mark as Complete
        </Button>
      </Box>

      <MarkCompleteModal />

      <GenericSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

const CounterControlModal = ({
  openModalCounterControl,
  modalData,
  handleCloseModalCounterControl,
  isAddNew,
  modalStatKey,
  cardDetails,
  modalTeam,
  setCardDetails,
  setCounts,
  players = [],
}) => {
  const [localModalData, setLocalModalData] = useState([]);

  const convertTimeToMinutes = (hhmmss) => {
    if (!hhmmss) return "";
    const [hours, minutes] = hhmmss.split(":");
    return String(parseInt(hours, 10) * 60 + parseInt(minutes, 10));
  };
  const isUpdateDisabled = localModalData.some((item) => {
    const isShootout = modalStatKey === "penalty" && item.alias === "shootouts";
    const jerseyBad = !(parseInt(item.jersey_no) > 0);
    const outcomeBad =
      modalStatKey === "penalty" && typeof item.success !== "boolean";
    // Shootouts need jersey number + outcome (no time).
    if (isShootout) return jerseyBad || outcomeBad;
    const timeBad = !(parseInt(item.time) > 0);
    // Injury time is only valid at the end of a half (45' or 90').
    const injuryBad =
      parseInt(item.injury_time) > 0 &&
      ![45, 90].includes(parseInt(item.time, 10));
    return jerseyBad || timeBad || outcomeBad || injuryBad;
  });

  const formatTime = (minuteStr) => {
    const mins = parseInt(minuteStr || "0", 10);
    const hours = Math.floor(mins / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (mins % 60).toString().padStart(2, "0");
    return `${hours}:${minutes}:00`;
  };

  const handleDeleteItem = (index) => {
    setLocalModalData((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (openModalCounterControl) {
      const converted = modalData.map((item) => ({
        ...item,
        time: convertTimeToMinutes(item.time), // hh:mm:ss -> minutes
        injury_time: convertTimeToMinutes(item.injury_time), // hh:mm:ss -> minutes
      }));
      setLocalModalData(converted);
    } else {
      setLocalModalData([]);
    }
  }, [openModalCounterControl, modalData]);

  return (
    <Modal
      open={openModalCounterControl}
      onClose={handleCloseModalCounterControl}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          boxShadow: 24,
          p: 3,
          borderRadius: "8px",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" fontWeight="bold" mb={2}>
          {modalStatKey
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
          Details
        </Typography>

        {localModalData.map((item, index) => {
          const isShootout =
            modalStatKey === "penalty" && item.alias === "shootouts";
          const timeError =
            !isShootout && (!item.time || parseInt(item.time) <= 0);
          const jerseyError = !item.jersey_no || parseInt(item.jersey_no) <= 0;
          // Injury time is only allowed at the end of a half (45' or 90').
          const injuryAllowed = [45, 90].includes(parseInt(item.time, 10));
          const injuryError = parseInt(item.injury_time) > 0 && !injuryAllowed;

          return (
            <Box key={index} mb={2}>
              {index !== 0 && <Divider sx={{ mb: 2 }} />}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                  {`${stats[modalStatKey] || "Event"}${
                    isAddNew ? "" : ` ${index + 1}`
                  } Time`}
                </Typography>

                {!isAddNew && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteItem(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter time in mins (e.g. 45, 75, 120)"
                value={item.time}
                error={timeError}
                helperText={timeError ? "Enter valid time > 0" : ""}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "").slice(0, 3);
                  if (value && parseInt(value, 10) > 120) value = "120";
                  setLocalModalData((prev) =>
                    prev.map((el, i) =>
                      i === index ? { ...el, time: value } : el
                    )
                  );
                }}
                inputProps={{ maxLength: 3 }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "var(--surface-2)",
                    fontSize: "12px",
                    color: "var(--text-1)",
                  },
                  "& input": { fontSize: "12px", color: "var(--text-1)" },
                  "& input::placeholder": {
                    color: "var(--text-2)",
                    opacity: 1,
                    fontSize: "12px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-1)",
                  },
                }}
              />

              <Typography variant="subtitle2" fontWeight="medium" mt={2} mb={1}>
                Injury Time <Typography variant="caption" color="var(--text-2)">(optional)</Typography>
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                size="small"
                error={injuryError}
                helperText={
                  injuryError ? "Injury time allowed only at 45' or 90'" : ""
                }
                placeholder="Enter injury time in mins (e.g. 2, 5)"
                value={item.injury_time || ""}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "").slice(0, 2);
                  if (value && parseInt(value, 10) > 30) value = "30";
                  setLocalModalData((prev) =>
                    prev.map((el, i) =>
                      i === index ? { ...el, injury_time: value } : el
                    )
                  );
                }}
                inputProps={{ maxLength: 2 }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "var(--surface-2)",
                    fontSize: "12px",
                    color: "var(--text-1)",
                  },
                  "& input": { fontSize: "12px", color: "var(--text-1)" },
                  "& input::placeholder": {
                    color: "var(--text-2)",
                    opacity: 1,
                    fontSize: "12px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--border-1)",
                  },
                }}
              />

              <Typography variant="subtitle2" fontWeight="medium" mt={2} mb={1}>
                Jersey No.
              </Typography>

              <Autocomplete
                fullWidth
                size="small"
                options={players}
                getOptionLabel={(o) => o.label || ""}
                isOptionEqualToValue={(o, v) => o.jersey_no === v.jersey_no}
                value={
                  players.find((p) => p.jersey_no === item.jersey_no) || null
                }
                onChange={(_, selected) => {
                  const value = selected?.jersey_no || "";
                  setLocalModalData((prev) =>
                    prev.map((el, i) =>
                      i === index ? { ...el, jersey_no: value } : el
                    )
                  );
                }}
                noOptionsText="No players found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Search jersey number / player"
                    error={jerseyError}
                    helperText={jerseyError ? "Select a player" : ""}
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "var(--surface-2)",
                        fontSize: "12px",
                        color: "var(--text-1)",
                      },
                      "& input": { fontSize: "12px", color: "var(--text-1)" },
                      "& input::placeholder": {
                        color: "var(--text-2)",
                        opacity: 1,
                        fontSize: "12px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-1)",
                      },
                    }}
                  />
                )}
              />

              {modalStatKey === "penalty" && (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight="medium"
                    mt={2}
                    mb={1}
                  >
                    Penalty Type
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={item.alias || "in_match"}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLocalModalData((prev) =>
                        prev.map((el, i) =>
                          i === index ? { ...el, alias: value } : el
                        )
                      );
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "var(--surface-2)",
                        fontSize: "12px",
                        color: "var(--text-1)",
                      },
                      "& select": {
                        fontSize: "12px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-1)",
                      },
                    }}
                  >
                    <MenuItem value="in_match">In Match</MenuItem>
                    <MenuItem value="shootouts">Shootouts</MenuItem>
                  </TextField>

                  <Box display="flex" gap={2} mt={1}>
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={item.success === true}
                          onChange={(e) => {
                            const value = e.target.checked ? true : undefined;
                            setLocalModalData((prev) =>
                              prev.map((el, i) =>
                                i === index ? { ...el, success: value } : el
                              )
                            );
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle2"
                          fontWeight="medium"
                          sx={{ color: "var(--text-1)" }}
                        >
                          Successful
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={item.success === false}
                          onChange={(e) => {
                            const value = e.target.checked ? false : undefined;
                            setLocalModalData((prev) =>
                              prev.map((el, i) =>
                                i === index ? { ...el, success: value } : el
                              )
                            );
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="subtitle2"
                          fontWeight="medium"
                          sx={{ color: "var(--text-1)" }}
                        >
                          Missed
                        </Typography>
                      }
                    />
                  </Box>
                  {typeof item.success !== "boolean" && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#d32f2f", display: "block", mt: 0.5 }}
                    >
                      Select penalty outcome
                    </Typography>
                  )}
                </>
              )}
            </Box>
          );
        })}

        <Box display="flex" width="100%" mt={2}>
          <Button
            disabled={isUpdateDisabled}
            variant="contained"
            sx={{
              textTransform: "none",
              flex: 1,
              backgroundColor: "#1A56DB",
            }}
            onClick={() => {
              const rawFormattedData = localModalData.map((item) => {
                const formatted = {
                  time: formatTime(item.time),
                  jersey_no: String(Number(item.jersey_no)),
                  injury_time: formatTime(item.injury_time),
                };

                if (modalStatKey === "penalty") {
                  formatted.alias = item.alias || "in_match";
                  formatted.success = item.success === true;
                }

                return formatted;
              });

              const validFormattedData = rawFormattedData.filter(
                (item) =>
                  item.jersey_no !== "0" &&
                  (item.alias === "shootouts" || item.time !== "00:00:00")
              );

              const existing = cardDetails[modalTeam]?.[modalStatKey] || [];

              let newData;

              if (isAddNew) {
                // ADDITION MODE — newest tagged goes to the top.
                newData = [...validFormattedData, ...existing];
              } else {
                // EDIT MODE — just use what's currently present in localModalData
                newData = validFormattedData;
              }

              // Identity of an event: same player, same timestamp, same
              // injury time (treat a missing injury time as "00:00:00").
              const eventKey = (o) =>
                `${o.jersey_no}|${o.time}|${o.injury_time || "00:00:00"}`;

              // A penalty counts as a goal only when it's a successful
              // in-match penalty (shootout penalties don't affect the score).
              const isPenaltyGoal = (p) =>
                p.success === true && (p.alias || "in_match") === "in_match";

              const teamDetails = { ...cardDetails[modalTeam] };
              teamDetails[modalStatKey] = newData;

              if (modalStatKey === "penalty") {
                // Mirror successful in-match penalties into the goal list.
                const prevDerivedKeys = new Set(
                  (existing || []).filter(isPenaltyGoal).map(eventKey)
                );

                // Keep manually-tagged goals; drop previously auto-created ones.
                const baseGoals = (cardDetails[modalTeam]?.goal || []).filter(
                  (g) => !prevDerivedKeys.has(eventKey(g))
                );
                const baseGoalKeys = new Set(baseGoals.map(eventKey));

                const penaltyGoals = newData
                  .filter(isPenaltyGoal)
                  .map(({ time, jersey_no, injury_time }) => ({
                    time,
                    jersey_no,
                    injury_time,
                  }))
                  .filter((g) => !baseGoalKeys.has(eventKey(g)));

                teamDetails.goal = [...baseGoals, ...penaltyGoals];
              }

              if (modalStatKey === "goal") {
                // Deleting a goal that came from a penalty removes that penalty.
                const newGoalKeys = new Set(newData.map(eventKey));
                const deletedGoalKeys = new Set(
                  (cardDetails[modalTeam]?.goal || [])
                    .filter((g) => !newGoalKeys.has(eventKey(g)))
                    .map(eventKey)
                );

                teamDetails.penalty = (
                  cardDetails[modalTeam]?.penalty || []
                ).filter(
                  (p) =>
                    !(isPenaltyGoal(p) && deletedGoalKeys.has(eventKey(p)))
                );
              }

              setCardDetails({
                ...cardDetails,
                [modalTeam]: teamDetails,
              });

              // Re-sync counts for every card stat that may have changed.
              setCounts((prev) => {
                const teamCounts = { ...prev[modalTeam] };
                ["goal", "yellow_card", "red_card", "penalty", "own_goal"].forEach(
                  (k) => {
                    if (Array.isArray(teamDetails[k]))
                      teamCounts[k] = teamDetails[k].length;
                  }
                );
                return { ...prev, [modalTeam]: teamCounts };
              });

              handleCloseModalCounterControl();
            }}
          >
            Update
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default LiveTaggingBoard;

